import styled, { css } from 'styled-components'
import type { ApparelProductProps } from '@velasca/types'
import { PriceRow as UIPriceRow } from '@velasca/ui/price-row'
import { ProductBranding } from '@velasca/ui/product-branding'
import { QuickAddToCartButton } from '@velasca/ui/quick-add-to-cart-button'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { SNAP_TRANSITION_MS } from './useCardImageSwipe.utils'

export const ApparelCard = styled.div<{
  $isQuickAdd: boolean
  layout: ApparelProductProps['layout']
}>`
  ${({ layout, theme, $isQuickAdd }) =>
    layout === 'list' ||
    layout === 'condensed-list' ||
    layout === 'ultra-condensed-list'
      ? `
    display: grid;
    grid-template-columns: ${
      layout === 'ultra-condensed-list'
        ? '25.5%'
        : layout === 'condensed-list'
          ? '38.5%'
          : 'minmax(auto, 26.5%)'
    } auto;
    height: fit-content;
  padding: ${layout === 'ultra-condensed-list' ? '0.4rem 0' : '1.2rem 0'};
    border-bottom: 1px solid ${theme.colors.secondary};

    &:first-child {
      padding-top: 0;
    }
  `
      : `
    display: flex;
    flex-direction: column;
    @media screen and (min-width: 768px) {
      &:hover:before {
        content: '';
        position: absolute;
        background-color: ${$isQuickAdd ? theme.colors.background : 'inherit'};
        box-shadow: ${$isQuickAdd ? '0 0.4rem 2.2rem rgba(0, 0, 0, 0.03)' : 'none'};
      }
    }
  `}
  position: relative;
  width: 100%;
`

export const Link = styled.a`
  color: unset !important;

  &:hover,
  &:link,
  &:visited,
  &:active {
    text-decoration: none;
  }
`

export const LinkFlex = styled(Link)<{
  layout: ApparelProductProps['layout']
  hasSole: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: start;
  ${({ layout }) =>
    layout === 'condensed-list' && 'padding-top: 0; padding-bottom: 0;'};
  padding: ${({ layout, hasSole }) =>
    layout === 'ultra-condensed-list'
      ? hasSole
        ? '0.3rem 1rem'
        : '0.5rem 1rem'
      : layout === 'search'
        ? '1rem 0'
        : '1.5rem 1rem'};

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    outline: none !important;
  }

  &:focus:not(:focus-visible) {
    outline: none !important;
  }

  &:focus h3,
  &:focus-visible h3 {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    outline-offset: 2px !important;
  }

  &:focus:not(:focus-visible) h3 {
    outline: none !important;
  }
`

export const ProductDetailsWrapper = styled.div<{
  layout: ApparelProductProps['layout']
}>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? 'column'
      : 'row'};
  flex-grow: 1;
  gap: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? '2rem'
      : '1rem'};
  ${({ layout }) =>
    layout !== 'condensed-list' &&
    layout !== 'ultra-condensed-list' &&
    'justify-content: space-between;'};
  align-items: start;

  .apparel-product-price-row {
    margin-top: 0;
  }
`

export const ProductDescriptionsWrapper = styled.div<{
  layout: ApparelProductProps['layout']
}>`
  flex-grow: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? '0'
      : '3'};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

// TODO: Here we didn't implemented the `SerifTitle` component beacause we have custom sizes, we should chang design at least for the `dynamic-size` to satisfy the `SerifTitle` component.
export const Title = styled.h3<{ layout: ApparelProductProps['layout'] }>`
  display: block;
  width: 100%;
  font-family: ${({ theme }) => theme.fonts.serif.fontFamily};
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.primary};

  > span span {
    font-size: 2.3rem;
  }

  font-size: ${({ layout }) =>
    layout === 'search' || layout === 'ultra-condensed-list'
      ? '2rem'
      : '2.3rem'};
  line-height: ${({ layout }) =>
    layout === 'ultra-condensed-list'
      ? '1'
      : layout === 'search'
        ? '125%'
        : '2.3rem'};
`

export const StyledProductBranding = styled(ProductBranding)``

export const Img = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  object-fit: cover;
  width: 100%;
`

export const HoverImg = styled(Img)<{ $swipeEnabled?: boolean }>`
  display: none;

  @media (hover: hover) and (pointer: fine) {
    display: block;
  }

  ${({ $swipeEnabled }) =>
    $swipeEnabled &&
    css`
      @media (hover: none) {
        display: block;
      }
    `}
`

export const AbsoluteQuickAddToCartButton = styled(QuickAddToCartButton)`
  z-index: 1;
  position: absolute;
  bottom: 0.75rem;
  right: 0.25rem;
`

interface ImgWrapperProps {
  visible: boolean
  $swipeEnabled?: boolean
}

// A wrapper needs `position: absolute; inset: 0` BEFORE it ever gets a
// `transform`: a non-none transform makes the element a containing block for
// its absolutely-positioned `Img` child, and without explicit dimensions of
// its own the wrapper (and therefore the image) collapses to zero size.
// `will-change: transform` is intentionally NOT set here: it's a transient
// compositor-layer hint applied only while actively dragging, via the inline
// style from useCardImageSwipe — setting it statically here would promote a
// layer for the lifetime of every swipe-enabled card on a dense mobile grid.
const swipeLayerStyles = css`
  position: absolute;
  inset: 0;
  transition: transform ${SNAP_TRANSITION_MS}ms ease;
`

const ImgWrapper = styled.div<ImgWrapperProps>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};

  ${({ $swipeEnabled }) => $swipeEnabled && swipeLayerStyles}
`

export const HoverImgWrapper = styled(ImgWrapper)`
  opacity: 0;

  ${({ $swipeEnabled }) => $swipeEnabled && 'opacity: 1;'}
`
export const FrontImgWrapper = styled(ImgWrapper)`
  opacity: 1;
`

export const UpperSection = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;

  > span {
    display: block;
    line-height: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${HoverImgWrapper} {
        opacity: 1;
      }

      ${FrontImgWrapper}:not(.single) {
        opacity: 0;
      }
    }
  }
`

export const Images = styled.div<{ $swipeEnabled?: boolean }>`
  position: relative;
  aspect-ratio: 4/5;
  /* pan-y lets vertical page scroll start immediately; horizontal is claimed
     imperatively (preventDefault) once useCardImageSwipe's axis-lock decides
     the gesture is a swipe, so native horizontal panning never fights it. */
  touch-action: ${({ $swipeEnabled }) => ($swipeEnabled ? 'pan-y' : 'auto')};

  .safari-lower-than-15 & {
    padding-top: 125%;
  }
`

// export const ProductReviewBadge = styled(ReviewBadge)<{ current: boolean }>`
//   display: ${({ current }) => (current ? 'flex' : 'none')};
//   margin-bottom: 1rem;
//   align-items: baseline;
// `

export const ProductInfo = styled.div<{
  layout: ApparelProductProps['layout']
  isPriceDiscounted: boolean
}>`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 500;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ layout }) => layout === 'card' && 'max-width: 90%;'};
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.light};

  max-width: ${({ isPriceDiscounted }) =>
    isPriceDiscounted ? '150px' : '220px'};

  > span {
    font-size: 1.2rem;
    line-height: 1.8rem;
  }

  font-size: 1.2rem;
  line-height: 1.8rem;

  @media screen and (min-width: 768px) {
    line-height: ${({ layout }) =>
      layout === 'ultra-condensed-list' ? '1' : '1.5rem'};
    font-size: 1.2rem;

    > span span {
      line-height: ${({ layout }) =>
        layout === 'ultra-condensed-list' ? '1' : '1.5rem'};
      font-size: 1.2rem;
    }
  }
`

export const Season = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`

export const SeasonLabel = styled.span`
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.4rem;
  line-height: normal;
`

export const Price = styled.span<{ isDiscounted?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 500;
  color: ${({ theme, isDiscounted }) =>
    isDiscounted ? theme.colors.hover : theme.colors.text.primary};
  font-size: 1.8rem !important;
  line-height: 1.8rem !important;
  white-space: nowrap;
  text-align: right;

  ${({ isDiscounted }) =>
    isDiscounted &&
    css`
      text-decoration: line-through;
      text-decoration-thickness: 1px;
      font-weight: 400;
      font-size: 1.6rem !important;
    `}
`

export const Description = styled.div<{
  layout: ApparelProductProps['layout']
  hasSeoCaption?: boolean
}>`

  ${({ layout }) =>
    layout === 'list' ||
    layout === 'condensed-list' ||
    layout === 'ultra-condensed-list'
      ? `
      display: flex;
      flex-direction: column;
    `
      : `
      display: inline-block;
    `}
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  /* With a seo_caption above, tighten line-height to the font size so this block
     has no extra top leading (like the title, whose line-height == font), making
     the caption↔color gap equal the title↔caption gap. Set in BOTH the base and
     the @media: stylis emits the @media after the base block, so a 2rem media
     rule would otherwise re-win on desktop. */
  line-height: ${({ hasSeoCaption }) => (hasSeoCaption ? '1.4rem' : '2rem')};

  @media screen and (min-width: 768px) {
    line-height: ${({ hasSeoCaption }) => (hasSeoCaption ? '1.4rem' : '2rem')};
  }

  span {
    font-family: ${({ theme }) => theme.fonts.brand};
    font-size: 1.4rem;
    line-height: inherit;

    ${({ layout }) =>
      (layout === 'list' ||
        layout === 'condensed-list' ||
        layout === 'ultra-condensed-list') &&
      `

      &.separator {
        display: none;
      }
    `}
`

export const DescriptionBold = styled.span`
  font-weight: 500;
`

export const DescriptionThin = styled.span`
  font-weight: 400;
`

export const DescriptionBlock = styled(DescriptionThin)`
  display: block;
  width: 100%;
`

export const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 400;
  font-size: 1.2rem;
  line-height: normal;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.light};
`

export const SlideUp = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`

export const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 0.5rem;
`

export const StyledPriceRow = styled(UIPriceRow)<{
  layout: ApparelProductProps['layout']
}>`
  ${({ layout }) =>
    layout === 'ultra-condensed-list'
      ? `
    position: absolute;
    top: 0;
    right: 0;
  `
      : ''}
`
